'use client'

import { useSelector } from '@/hooks/redux'
import { usePathname } from '@/navigation'
import { setPath } from '@/reducers/workflow'
import {
  commonFlowSteps,
  Formalities,
  ModuleType,
  paymentSteps
} from '@/workflows/modules'
import { Steps } from 'antd'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { GET_SOCIETY, SocietyDataType } from '@/graphql/queries/society'
import { useQuery } from '@apollo/client'

interface BreadcrumbProps {}

const Breadcrumb: React.FC<BreadcrumbProps> = () => {
  const dispatch = useDispatch()
  const t = useTranslations()
  const pathname = usePathname()
  const workflow = useSelector((state) => state.workflow)
  const { data: dataSociety } = useQuery<SocietyDataType>(GET_SOCIETY)

  const flow = workflow.flow
  const flowType = workflow.flowType
  const availableFormality =
    !!dataSociety?.society?.formality?.identificationToken
  const hideDomideal = sessionStorage.getItem('hideDomideal') === '1'

  const [flowSteps, setFlowSteps] = useState<ModuleType[]>([])
  const [currentStep, setCurrentStep] = useState(
    flowSteps.findIndex((item: ModuleType) => item.path === pathname)
  )

  const breadcrumbBuilder = () => {
    const steps = [...commonFlowSteps]
    if (availableFormality && !hideDomideal) steps.push(Formalities)
    steps.push(...paymentSteps)

    return steps
  }

  useEffect(() => {
    if (flowType === 'create_or_transfert') return
    if (workflow.path === '/welcome' || workflow.path === '/payment/processing')
      return setFlowSteps([])
    return setFlowSteps(breadcrumbBuilder())
  }, [flowType, availableFormality, hideDomideal])

  useEffect(() => {
    setCurrentStep(
      flowSteps.findIndex((item: ModuleType) => item.path === pathname)
    )
  }, [pathname, flowSteps])

  const highestReachedIndex = flowSteps.reduce((maxIndex, step, index) => {
    if (flow.includes(step.path)) {
      return Math.max(maxIndex, index)
    }
    return maxIndex
  }, -1)

  const isStepAccessible = (index: number) => {
    return flow.includes(flowSteps[index].path) || index <= highestReachedIndex
  }

  const onChange = (value: number) => {
    const newPath = flowSteps[value].path
    if (isStepAccessible(value)) {
      dispatch(setPath(newPath))
    }
  }

  const getStatus = (index: number) => {
    if (index === currentStep) return 'process'
    if (isStepAccessible(index)) return 'finish'
    return 'wait'
  }

  useEffect(() => {
    const processStep = document.querySelector('.ant-steps-item-process')
    const finishSteps = document.querySelectorAll('.ant-steps-item-finish')

    if (processStep) {
      processStep
        .querySelector('.ant-steps-item-title')!
        .classList.add('!text-interaction', 'font-semibold')
      finishSteps.forEach((item) =>
        item
          .querySelector('.ant-steps-item-title')
          ?.classList.remove('!text-interaction', 'font-semibold')
      )
      return
    }
    return
  }, [currentStep])

  return (
    <Steps
      data-testid='subflow-breadcrumb'
      progressDot
      className='h-fit w-max! [&_.ant-steps-item-active]:text-interaction'
      direction='vertical'
      onChange={onChange}
      current={currentStep}
      items={flowSteps.map((item, index) => {
        let stepTitle: React.ReactNode = t(`breadcrumb.${item.name}`)
        if (
          item.name === 'legalStatus' &&
          dataSociety?.society?.legalStatus?.name
        ) {
          stepTitle = (
            <div className='flex flex-col mt-[-4px]'>
              <span className='leading-tight'>
                {t(`breadcrumb.${item.name}`)} :
              </span>
              <span className='text-[16px] opacity-70 leading-none'>
                {dataSociety.society.legalStatus.commonName ||
                  dataSociety.society.legalStatus.name}
              </span>
            </div>
          )
        }
        return {
          title: stepTitle,
          status: getStatus(index),
          disabled: !isStepAccessible(index)
        }
      })}
    />
  )
}

export default Breadcrumb
